import { useState, useRef, useCallback } from 'react';

const useTooltip = ({ enterDelay = 200, leaveDelay = 100, placement = 'top' } = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [position, setPosition] = useState({ top: 0, left: 0 });
  const enterTimer = useRef(null);
  const leaveTimer = useRef(null);
  const containerRef = useRef(null);

  const clearTimers = useCallback(() => {
    clearTimeout(enterTimer.current);
    clearTimeout(leaveTimer.current);
  }, []);

  const updatePosition = useCallback(() => {
    if (!containerRef.current) return;
    const anchor = containerRef.current.getBoundingClientRect().width
      ? containerRef.current
      : containerRef.current.firstElementChild;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const gap = 8;

    const positionMap = {
      top: { top: rect.top - gap, left: rect.left + rect.width / 2 },
      bottom: { top: rect.bottom + gap, left: rect.left + rect.width / 2 },
      left: { top: rect.top + rect.height / 2, left: rect.left - gap },
      right: { top: rect.top + rect.height / 2, left: rect.right + gap },
    };

    setPosition(positionMap[placement] || positionMap.top);
  }, [placement]);

  const handleMouseEnter = useCallback(() => {
    clearTimers();
    updatePosition();
    enterTimer.current = setTimeout(() => setIsVisible(true), enterDelay);
  }, [enterDelay, clearTimers, updatePosition]);

  const handleMouseLeave = useCallback(() => {
    clearTimers();
    leaveTimer.current = setTimeout(() => setIsVisible(false), leaveDelay);
  }, [leaveDelay, clearTimers]);

  const containerProps = {
    ref: containerRef,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onFocus: handleMouseEnter,
    onBlur: handleMouseLeave,
  };

  return { isVisible, position, containerProps };
};

export default useTooltip;
